import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsAPI, type BizGroupRow } from '@matix/api-client';
import {
  ReportToolbar, Field, DateTimeRange, fmt2, fmtMoney, fmtInt, fmtPct,
  todayFrom, todayTo, rangeParams, rangeLabel, useLabelOf, printableHtml,
} from './reportShared';
import { printReport, type ReportPaper } from './reportPrint';
import { ReportSheetBuilder, downloadReportExcel } from './reportExcel';
import { useLang } from '../../lib/language';

export function BusinessSummaryReport(): JSX.Element {
  const { t, lang } = useLang();
  const labelOf = useLabelOf();
  const printRef = useRef<HTMLDivElement>(null);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [from, setFrom] = useState(todayFrom);
  const [to, setTo] = useState(todayTo);
  const [params, setParams] = useState<
    { branch_id: number; from: string; to: string; from_time?: string; to_time?: string } | null
  >(null);

  const filtersQ = useQuery({ queryKey: ['rpt-filters'], queryFn: () => reportsAPI.filters() });
  // Own branch only, preselected (2026-09-08) — the server pins the query to
  // it as well. See QuickSalesReport for the rationale.
  const ownBranchId = filtersQ.data?.default_branch_id ?? null;
  const branches = useMemo(
    () => (filtersQ.data?.branches ?? []).filter((b) => ownBranchId == null || b.id === ownBranchId),
    [filtersQ.data, ownBranchId],
  );
  useEffect(() => {
    if (filtersQ.data && branchId === '') {
      setBranchId(ownBranchId ?? branches[0]?.id ?? '');
    }
  }, [filtersQ.data, branchId, ownBranchId, branches]);

  const dataQ = useQuery({
    queryKey: ['business-summary', params],
    queryFn: () => reportsAPI.businessSummary(params!),
    enabled: params != null,
  });

  const range = rangeLabel(from, to);
  const title = t({ en: 'Business Summary Report', ar: 'تقرير ملخص الأعمال' });

  function run(): void {
    const r = rangeParams(from, to);
    if (branchId === '' || !r) return;
    setParams({ branch_id: branchId, ...r });
  }
  function exportPdf(paper: ReportPaper): void { printReport(title, range, printableHtml(printRef), paper); }

  const d = dataQ.data;
  const branchName = filtersQ.data?.branches.find((b) => b.id === branchId);
  const scope = labelOf(branchName ?? { name: '', name_ar: null });

  function exportExcel(): void {
    if (!d) return;
    const label = t({ en: 'Total', ar: 'الإجمالي' });
    const groupCols = (first: string) => [
      { label: first },
      { label: t({ en: 'Qty', ar: 'الكمية' }), kind: 'qty' as const },
      { label: t({ en: 'Qty %', ar: 'الكمية %' }), kind: 'pct' as const },
      { label: t({ en: 'Value', ar: 'القيمة' }), kind: 'money' as const },
      { label: t({ en: 'Value %', ar: 'القيمة %' }), kind: 'pct' as const },
    ];
    const b = new ReportSheetBuilder().title(title, `${scope} · ${range}`);

    b.section(t({ en: 'Revenues', ar: 'الإيرادات' }))
      .table(
        [{ label: '' }, { label: '', kind: 'money' }],
        [
          [t({ en: 'Sales', ar: 'المبيعات' }), d.revenues.sales],
          // A count, not money — it overrides the column's currency format.
          [t({ en: 'Total Orders', ar: 'إجمالي الطلبات' }), { v: d.revenues.total_orders, kind: 'int' as const }],
          [t({ en: 'Total Discount', ar: 'إجمالي الخصم' }), d.revenues.discount],
          [t({ en: 'Delivery Fees', ar: 'رسوم التوصيل' }), d.revenues.delivery_fees],
          [t({ en: 'Total Tax', ar: 'إجمالي الضريبة' }), d.revenues.tax],
        ],
        { header: false, totals: [t({ en: 'Net Sales', ar: 'صافي المبيعات' }), d.revenues.net_sales] },
      );

    b.section(t({ en: 'Voids', ar: 'الملغاة' }))
      .table(
        [
          { label: t({ en: 'Type', ar: 'النوع' }) },
          { label: t({ en: 'Qty', ar: 'الكمية' }), kind: 'qty' },
          { label: t({ en: 'Value', ar: 'القيمة' }), kind: 'money' },
        ],
        [
          [t({ en: 'Orders', ar: 'الطلبات' }), d.voids.orders_qty, d.voids.orders_value],
          [t({ en: 'Items', ar: 'الأصناف' }), d.voids.items_qty, d.voids.items_value],
        ],
      );

    const groups: [string, string, typeof d.order_types][] = [
      [t({ en: 'Sales by Order Type', ar: 'المبيعات حسب نوع الطلب' }), t({ en: 'Type', ar: 'النوع' }), d.order_types],
      [t({ en: 'Sales by Payment Type', ar: 'المبيعات حسب نوع الدفع' }), t({ en: 'Payment', ar: 'الدفع' }), d.payment_types],
      [t({ en: 'Sales by Staff', ar: 'المبيعات حسب الموظف' }), t({ en: 'Employee', ar: 'الموظف' }), d.staff],
      [t({ en: 'Sales by Category', ar: 'المبيعات حسب الفئة' }), t({ en: 'Category', ar: 'الفئة' }), d.categories],
    ];
    for (const [heading, firstCol, rows] of groups) {
      b.section(heading).table(
        groupCols(firstCol),
        rows.map((r) => [labelOf({ name: r.metric, name_ar: r.metric_ar }), r.qty, r.qty_pct, r.value, r.value_pct]),
        {
          emptyText: t({ en: 'No data', ar: 'لا توجد بيانات' }),
          totals: rows.length === 0 ? undefined : [
            label,
            rows.reduce((s, r) => s + r.qty, 0),
            null,
            rows.reduce((s, r) => s + r.value, 0),
            null,
          ],
        },
      );
    }
    downloadReportExcel(`${title} ${range}`, b.build(title, { rightToLeft: lang === 'ar' }));
  }

  return (
    <div className="rpt-wrap">
      <ReportToolbar
        running={dataQ.isFetching}
        canExport={!!d}
        onRun={run}
        onExport={exportPdf}
        onExportExcel={exportExcel}
        filters={
          <>
            <Field label={t({ en: 'Branch', ar: 'الفرع' })}>
              <select value={branchId} onChange={(e) => setBranchId(e.target.value ? Number(e.target.value) : '')}>
                {branches.map((b) => <option key={b.id} value={b.id}>{labelOf(b)}</option>)}
              </select>
            </Field>
            <DateTimeRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
          </>
        }
      />

      {dataQ.error && <div className="rpt-error">{t({ en: 'Failed to load report. Try again.', ar: 'تعذّر تحميل التقرير. حاول مجدداً.' })}</div>}

      <div className="rpt-print" ref={printRef}>
        {d && (
          <>
            <div className="rpt-meta">{scope} · {range}</div>

            <div className="rpt-two-col">
              <div>
                <h2 className="section">{t({ en: 'Revenues', ar: 'الإيرادات' })}</h2>
                <table>
                  <tbody>
                    <tr><td>{t({ en: 'Sales', ar: 'المبيعات' })}</td><td className="num">{fmtMoney(d.revenues.sales)}</td></tr>
                    <tr><td>{t({ en: 'Total Orders', ar: 'إجمالي الطلبات' })}</td><td className="num">{fmtInt(d.revenues.total_orders)}</td></tr>
                    <tr><td>{t({ en: 'Total Discount', ar: 'إجمالي الخصم' })}</td><td className="num">{fmtMoney(d.revenues.discount)}</td></tr>
                    <tr><td>{t({ en: 'Delivery Fees', ar: 'رسوم التوصيل' })}</td><td className="num">{fmtMoney(d.revenues.delivery_fees)}</td></tr>
                    <tr><td>{t({ en: 'Total Tax', ar: 'إجمالي الضريبة' })}</td><td className="num">{fmtMoney(d.revenues.tax)}</td></tr>
                    <tr className="total-row"><td>{t({ en: 'Net Sales', ar: 'صافي المبيعات' })}</td><td className="num">{fmtMoney(d.revenues.net_sales)}</td></tr>
                  </tbody>
                </table>
              </div>
              <div>
                <h2 className="section">{t({ en: 'Voids', ar: 'الملغاة' })}</h2>
                <table>
                  <thead><tr><th>{t({ en: 'Type', ar: 'النوع' })}</th><th className="num">{t({ en: 'Qty', ar: 'الكمية' })}</th><th className="num">{t({ en: 'Value', ar: 'القيمة' })}</th></tr></thead>
                  <tbody>
                    <tr><td>{t({ en: 'Orders', ar: 'الطلبات' })}</td><td className="num">{fmtInt(d.voids.orders_qty)}</td><td className="num">{fmtMoney(d.voids.orders_value)}</td></tr>
                    <tr><td>{t({ en: 'Items', ar: 'الأصناف' })}</td><td className="num">{fmt2(d.voids.items_qty)}</td><td className="num">{fmtMoney(d.voids.items_value)}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <GroupSection title={t({ en: 'Sales by Order Type', ar: 'المبيعات حسب نوع الطلب' })} firstCol={t({ en: 'Type', ar: 'النوع' })} rows={d.order_types} />
            <GroupSection title={t({ en: 'Sales by Payment Type', ar: 'المبيعات حسب نوع الدفع' })} firstCol={t({ en: 'Payment', ar: 'الدفع' })} rows={d.payment_types} />
            <GroupSection title={t({ en: 'Sales by Staff', ar: 'المبيعات حسب الموظف' })} firstCol={t({ en: 'Employee', ar: 'الموظف' })} rows={d.staff} />
            <GroupSection title={t({ en: 'Sales by Category', ar: 'المبيعات حسب الفئة' })} firstCol={t({ en: 'Category', ar: 'الفئة' })} rows={d.categories} />
          </>
        )}
        {!d && !dataQ.isFetching && <div className="rpt-empty">{t({ en: 'Choose a branch, a date and time range, then “Show report”.', ar: 'اختر الفرع والتاريخ والوقت، ثم “عرض التقرير”.' })}</div>}
      </div>
    </div>
  );
}

function GroupSection({ title, firstCol, rows }: { title: string; firstCol: string; rows: BizGroupRow[] }): JSX.Element {
  const { t } = useLang();
  const pick = useLabelOf();
  return (
    <>
      <h2 className="section">{title}</h2>
      <table>
        <thead>
          <tr><th>{firstCol}</th><th className="num">{t({ en: 'Qty', ar: 'الكمية' })}</th><th className="num">{t({ en: 'Qty %', ar: 'الكمية %' })}</th><th className="num">{t({ en: 'Value', ar: 'القيمة' })}</th><th className="num">{t({ en: 'Value %', ar: 'القيمة %' })}</th></tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={5} className="rpt-empty">{t({ en: 'No data', ar: 'لا توجد بيانات' })}</td></tr>}
          {rows.map((r) => (
            <tr key={r.metric}>
              {/* Category / order type / payment type / staff names are tenant
                  data, so they come from the row's own pair of columns rather
                  than a t({en,ar}) string. */}
              <td>{pick({ name: r.metric, name_ar: r.metric_ar })}</td>
              <td className="num">{fmt2(r.qty)}</td>
              <td className="num">{fmtPct(r.qty_pct)}</td>
              <td className="num">{fmtMoney(r.value)}</td>
              <td className="num">{fmtPct(r.value_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
